/**
 * Live Chat Component
 *
 * YouTube-style live chat component for live events.
 * Uses signal-based DI for state management.
 *
 * Features:
 * - Real-time message display via SSE
 * - Message input with emoji picker (authenticated users)
 * - Login prompt (unauthenticated users)
 * - Loading, error, and empty states
 */

import { html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { linkifyText } from "ui-common/linkify";
import { SignalElement, inject } from "ui-common/di";
import { isSystemMessage, type IChatMessage, type IChatAttachment } from "ui-common/services/live-chat/types";
import type { ILiveChatState, ILiveChatService } from "ui-common/services/live-chat/types";
import { formatFileSize } from "helpers/fileValidation";
import "@phosphor-icons/webcomponents/PhDownload";
import type { ISseState } from "ui-common/services/sse/types";
import { getValue, getLang } from "ui-common/i18n";
import { liveChatStyles } from "./live-chat.css";
import { chatAttachmentStyles } from "../chat-message-input/chat-attachments.css";
import "../button/wow-button";
import "../chat-message-input/chat-message-input";
import type { ChatMessageInput } from "../chat-message-input/chat-message-input";
import "@phosphor-icons/webcomponents/PhHourglass";
import "@phosphor-icons/webcomponents/PhUsers";
import "@phosphor-icons/webcomponents/PhChats";
import "@phosphor-icons/webcomponents/PhX";
import "../event-badge/event-badge";
import { getAssetUrl } from "helpers/assetHelpers";
import { IOSKeyboardController } from "ui-common/ios-keyboard-controller";

const EMOJIS = ["❤️", "🔥", "👏", "😍", "🎉", "💯", "😂", "🙌", "✨", "💪", "👀", "🤩"];
const SCROLL_TOP_THRESHOLD = 20;

/**
 * Live chat component for live events.
 *
 * @fires show-login - Dispatched when unauthenticated user clicks login (not implemented yet)
 *
 * @example
 * <live-chat event-id="event-123" ?is-authenticated=${true}></live-chat>
 */
@customElement("live-chat")
export class LiveChat extends SignalElement {
  static override styles = [liveChatStyles, chatAttachmentStyles];

  /**
   * The live event ID to connect to.
   */
  @property({ type: String, attribute: "event-id" })
  eventId = "";

  /**
   * Whether the current user is authenticated.
   */
  @property({ type: Boolean, attribute: "is-authenticated" })
  isAuthenticated = false;

  /**
   * The live event status.
   * When "scheduled", shows a waiting message instead of chat input.
   */
  @property({ type: String, attribute: "event-status" })
  eventStatus: "scheduled" | "live" | "ended" | "cancelled" | "" = "";

  /**
   * Whether the event is free (allows anonymous chat viewing).
   * When true, unauthenticated users can view messages but not send them.
   */
  @property({ type: Boolean, attribute: "is-free-event" })
  isFreeEvent = false;

  /**
   * Current viewer/attendee count to display in the header.
   */
  @property({ type: Number, attribute: "viewer-count" })
  viewerCount = 0;

  /**
   * Whether the lobby is open (30 minutes before event start).
   * When true, chat is available even if event status is "scheduled".
   */
  @property({ type: Boolean, attribute: "is-lobby-open" })
  isLobbyOpen = false;

  @property({ type: Boolean, attribute: "hide-header-top" })
  hideHeaderTop = false;

  /**
   * Primary color for the viewer icon (creator's theme color).
   */
  @property({ type: String, attribute: "primary-color" })
  primaryColor = "";

  @property({ type: String, attribute: "banner-title" })
  bannerTitle = "Be respectful and follow community guidelines.";

  @property({ type: String, attribute: "banner-subtitle" })
  bannerSubtitle = "Your messages and name are visible to everyone.";

  /**
   * Whether to show a close button in the chat header.
   */
  @property({ type: Boolean, attribute: "show-close-button" })
  showCloseButton = false;

  /**
   * Whether to hide the viewer count badge in the header.
   */
  @property({ type: Boolean, attribute: "hide-viewer-count" })
  hideViewerCount = false;

  /**
   * Whether to hide the input area (message input or login prompt).
   * Used on the info page chat tab during live events to redirect users to the stream.
   */
  @property({ type: Boolean, attribute: "hide-input" })
  hideInput = false;

  /**
   * Whether the current user is the event owner/creator.
   * When true, shows the email notification toggle on the chat input.
   */
  @property({ type: Boolean, attribute: "is-event-owner" })
  isEventOwner = false;

  /**
   * Injected live chat state.
   * Component re-renders automatically when state signals change.
   */
  @inject("liveChatState")
  chatState!: ILiveChatState;

  /**
   * Injected live chat service.
   * Provides methods to connect, disconnect, and send messages.
   */
  @inject<ILiveChatService>("liveChatService")
  chatService!: ILiveChatService;

  /**
   * Injected SSE state.
   * Used to check connection status for send button state.
   */
  @inject<ISseState>("sseState")
  sseState!: ISseState;

  @state()
  private showEmojiPicker = false;

  @state()
  private activeViewerCount = 0;

  @state()
  private showGuidelinesBanner = true;

  private viewerCountPollTimer: ReturnType<typeof setInterval> | null = null;

  private bannerDismissTimer: ReturnType<typeof setTimeout> | null = null;

  private static readonly BANNER_DISMISS_MS = 5 * 60 * 1000;

  private static readonly BANNER_STORAGE_PREFIX = "chat-guidelines-dismissed-";

  private previousMessageCount = 0;
  private lastBottomMessageId?: string;
  private scrollThrottleTimer?: ReturnType<typeof setTimeout>;
  private prevScrollHeight = 0;

  /**
   * Observer to detect visibility changes for scroll restoration.
   */
  private resizeObserver?: ResizeObserver;

  /**
   * Track if component was hidden (width=0) to detect show transitions.
   */
  private wasHidden = false;

  /**
   * Whether the user can view chat messages.
   * True if authenticated.
   */
  private get canViewChat(): boolean {
    return this.isAuthenticated;
  }

  private get displayViewerCount(): number {
    return this.activeViewerCount > 0 ? this.activeViewerCount : this.viewerCount;
  }

  private get shouldPollViewerCount(): boolean {
    if (this.hideViewerCount) return false;
    if (!this.eventId) return false;
    if (this.eventStatus === "scheduled" && !this.isLobbyOpen) return false;
    return true;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.setupResizeObserver();
    if (this.eventId && this.canViewChat) {
      this.chatService?.connect(this.eventId);
      this.chatService?.setViewing(true);
    }
    this.startViewerCountPolling();
    if (this.isBannerDismissedForEvent()) {
      this.showGuidelinesBanner = false;
    } else {
      this.startBannerDismissTimer();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopViewerCountPolling();
    this.stopBannerDismissTimer();
    clearTimeout(this.scrollThrottleTimer);
    this.resizeObserver?.disconnect();
    this.chatService?.setViewing(false);
    this.chatService?.disconnect();
  }

  override firstUpdated(): void {
    this.resizeObserver?.observe(this);
    this.wasHidden = this.offsetWidth === 0;
  }

  override willUpdate(): void {
    const container = this.shadowRoot?.querySelector(".messages-container");
    if (container) {
      this.prevScrollHeight = container.scrollHeight;
    }
  }

  override updated(changedProps: Map<string, unknown>): void {
    super.updated(changedProps);
    const relevantChange =
      changedProps.has("eventId") || changedProps.has("isAuthenticated") || changedProps.has("isFreeEvent");

    if (relevantChange && this.eventId && this.canViewChat) {
      this.chatService?.connect(this.eventId);
    }

    if (changedProps.has("eventId") || changedProps.has("eventStatus") || changedProps.has("isLobbyOpen")) {
      this.activeViewerCount = 0;
      this.startViewerCountPolling();
    }

    if (changedProps.has("eventId") && this.eventId) {
      if (this.isBannerDismissedForEvent()) {
        this.showGuidelinesBanner = false;
        this.stopBannerDismissTimer();
      } else {
        this.showGuidelinesBanner = true;
        this.startBannerDismissTimer();
      }
    }

    // Handle scroll behavior when messages change
    const messages = this.chatState?.messages.get() ?? [];
    if (messages.length > this.previousMessageCount) {
      const wasLoadingOlder =
        this.previousMessageCount > 0 && messages[messages.length - 1]?.id === this.lastBottomMessageId;
      if (wasLoadingOlder) {
        // Older messages prepended: preserve scroll position
        const container = this.shadowRoot?.querySelector(".messages-container");
        if (container) {
          container.scrollTop += container.scrollHeight - this.prevScrollHeight;
        }
      } else {
        // New messages at bottom: auto-scroll down
        this.scrollToBottom();
      }
    }
    this.previousMessageCount = messages.length;
    this.lastBottomMessageId = messages[messages.length - 1]?.id;
  }

  /**
   * Scroll the messages container to the bottom.
   * Uses requestAnimationFrame to ensure scroll happens after render.
   */
  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const container = this.shadowRoot?.querySelector(".messages-container");
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  /**
   * Set up ResizeObserver to detect visibility changes.
   * When chat transitions from hidden (width=0) to visible, scroll to bottom.
   */
  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const isNowVisible = entry.contentRect.width > 0;

        if (this.wasHidden && isNowVisible) {
          this.scrollToBottom();
          this.chatService?.setViewing(true);
        } else if (!this.wasHidden && !isNowVisible) {
          this.chatService?.setViewing(false);
        }

        this.wasHidden = entry.contentRect.width === 0;
      }
    });
  }

  private startViewerCountPolling(): void {
    this.stopViewerCountPolling();
    if (!this.shouldPollViewerCount) return;
    this.fetchViewerCount();
    this.viewerCountPollTimer = setInterval(() => this.fetchViewerCount(), 10_000);
  }

  private stopViewerCountPolling(): void {
    if (this.viewerCountPollTimer !== null) {
      clearInterval(this.viewerCountPollTimer);
      this.viewerCountPollTimer = null;
    }
  }

  private startBannerDismissTimer(): void {
    this.stopBannerDismissTimer();
    this.bannerDismissTimer = setTimeout(() => {
      this.showGuidelinesBanner = false;
    }, LiveChat.BANNER_DISMISS_MS);
  }

  private stopBannerDismissTimer(): void {
    if (this.bannerDismissTimer !== null) {
      clearTimeout(this.bannerDismissTimer);
      this.bannerDismissTimer = null;
    }
  }

  private async fetchViewerCount(): Promise<void> {
    if (!this.eventId) return;
    try {
      const response = await fetch(`/api/viewer-count/${this.eventId}`);
      if (response.ok) {
        const data = (await response.json()) as { count: number };
        this.activeViewerCount = data.count;
      }
    } catch {
      // Silently ignore - viewer count is non-critical
    }
  }

  private async handleSendMessage(
    e: CustomEvent<{
      message: string;
      notifyByEmail: boolean;
      attachments?: Array<{ fileId: string; originalName: string; sizeBytes: number }>;
    }>,
  ): Promise<void> {
    const { message, notifyByEmail, attachments } = e.detail;
    if ((!message && !attachments?.length) || !this.chatService) return;

    await this.chatService.sendMessage(message, notifyByEmail, attachments);
    this.scrollToBottom();
    const chatInput = this.shadowRoot?.querySelector("chat-message-input") as ChatMessageInput | null;
    chatInput?.focus();
  }

  private handleInputFocusIn(): void {
    const container = this.shadowRoot?.querySelector(".messages-container");
    if (!container) return;
    const scrollToBottom = () => {
      container.scrollTop = container.scrollHeight;
    };
    // Scroll multiple times during the iOS keyboard animation (~300ms)
    // to keep last messages visible as the container height changes
    scrollToBottom();
    setTimeout(scrollToBottom, 150);
    setTimeout(scrollToBottom, 350);
  }

  private handleInputFocusOut(): void {
    const container = this.shadowRoot?.querySelector(".messages-container");
    IOSKeyboardController.fixScrollAfterKeyboardDismiss(container);
  }

  private handleEmojiToggle(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  private handleEmojiClick(emoji: string): void {
    this.showEmojiPicker = false;
    const chatInput = this.shadowRoot?.querySelector("chat-message-input") as ChatMessageInput | null;
    chatInput?.insertText(emoji);
  }

  private handleLoginClick(): void {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `/my/sign-in?return=${returnUrl}`;
  }

  private handleRetry(): void {
    if (this.eventId) {
      this.chatService?.connect(this.eventId);
    }
  }

  private handleMessagesScroll(e: Event): void {
    if (this.scrollThrottleTimer) return;
    const container = e.target as HTMLElement;
    if (container.scrollTop < SCROLL_TOP_THRESHOLD) {
      this.chatService?.loadOlderMessages();
    }
    this.scrollThrottleTimer = setTimeout(() => {
      this.scrollThrottleTimer = undefined;
    }, 200);
  }

  private formatTime(sentAt: string): string {
    try {
      const date = new Date(sentAt);
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  }

  private getInitials(name: string): string {
    return name.charAt(0).toUpperCase();
  }

  private renderLoading(): TemplateResult {
    return html`
      <div class="loading-container">
        <div class="loading-spinner"></div>
      </div>
    `;
  }

  private renderError(): TemplateResult {
    const errorMessage = this.chatState?.errorMessage.get() ?? "Failed to load chat";
    return html`
      <div class="error-container">
        <div class="error-message">${errorMessage}</div>
        <wow-button variant="primary" size="small" @click=${this.handleRetry}>Retry</wow-button>
      </div>
    `;
  }

  private renderEmpty(): TemplateResult {
    return html`
      <div class="empty-container">
        <span>No messages yet. Be the first to say something!</span>
      </div>
    `;
  }

  private renderWaitingForEvent(): TemplateResult {
    return html`
      <div class="waiting-container">
        <ph-hourglass class="waiting-icon" size="32" weight="duotone"></ph-hourglass>
        <p class="waiting-message">Chat will be available when the event starts</p>
      </div>
    `;
  }

  private renderSystemMessage(message: IChatMessage): TemplateResult {
    const text = getValue(getLang(), `liveChat.${message.content}`) || message.content;
    return html`
      <div class="system-message" data-message-id=${message.id}>
        <div class="system-message-divider"></div>
        <span class="system-message-text">${text}</span>
        <div class="system-message-divider"></div>
      </div>
    `;
  }

  private renderAttachment(a: IChatAttachment): TemplateResult {
    return html`
      <li class="attachment">
        <a class="attachment-link" href=${a.url} target="_blank" rel="noopener" download=${a.originalName}>
          <ph-download size="16" weight="bold"></ph-download>
          <span class="attachment-name" title=${a.originalName}>${a.originalName}</span>
          <span class="attachment-size">${formatFileSize(a.sizeBytes)}</span>
        </a>
      </li>
    `;
  }

  private renderMessage(message: IChatMessage): TemplateResult {
    const attachments = message.attachments ?? [];
    return html`
      <div class="message" data-message-id=${message.id}>
        <div class="message-avatar">
          ${message.userAvatarUrl
            ? html`<img src=${getAssetUrl(message.userAvatarUrl)} alt=${message.userName} />`
            : html`<div class="avatar-fallback">${this.getInitials(message.userName)}</div>`}
        </div>
        <div class="message-content">
          <div class="message-header">
            <span class="message-author">${message.userName}</span>
            <span class="message-time">${this.formatTime(message.sentAt)}</span>
          </div>
          ${message.content ? html`<div class="message-text">${linkifyText(message.content)}</div>` : nothing}
          ${attachments.length > 0
            ? html`<ul class="attachments">
                ${attachments.map((a) => this.renderAttachment(a))}
              </ul>`
            : nothing}
        </div>
      </div>
    `;
  }

  private renderMessages(): TemplateResult {
    const messages = this.chatState?.messages.get() ?? [];
    const loadingOlder = this.chatState?.loadingOlder.get() ?? false;

    if (messages.length === 0) {
      return this.renderEmpty();
    }
    return html`
      <div class="messages-container" @scroll=${this.handleMessagesScroll}>
        ${loadingOlder
          ? html`<div class="load-older-indicator">
              <div class="loading-spinner-small"></div>
            </div>`
          : nothing}
        ${messages.map((m) => (isSystemMessage(m) ? this.renderSystemMessage(m) : this.renderMessage(m)))}
      </div>
    `;
  }

  private renderLoginPrompt(): TemplateResult {
    return html`
      <div class="login-prompt">
        <span>Sign in to join the conversation</span>
        <wow-button variant="primary" @click=${this.handleLoginClick}>Sign In</wow-button>
      </div>
    `;
  }

  private renderEmojiPicker(): TemplateResult {
    return html`
      <div class="emoji-picker ${this.showEmojiPicker ? "" : "hidden"}">
        ${EMOJIS.map(
          (emoji) => html`
            <wow-button variant="subtle" size="small" @click=${() => this.handleEmojiClick(emoji)}>${emoji}</wow-button>
          `,
        )}
      </div>
    `;
  }

  private renderGuidelinesBanner(): TemplateResult {
    return html`
      <div class="guidelines-banner">
        <div class="banner-content">
          <p class="banner-title">${this.bannerTitle}</p>
          <p class="banner-subtitle">${this.bannerSubtitle}</p>
        </div>
        <div class="banner-actions">
          <button
            class="banner-close-button"
            @click=${this.dismissGuidelinesBanner}
            aria-label="Close guidelines"
            type="button"
          >
            <ph-x size="14"></ph-x>
          </button>
          <ph-chats class="banner-icon" size="24" weight="fill"></ph-chats>
        </div>
      </div>
    `;
  }

  private dismissGuidelinesBanner(): void {
    this.showGuidelinesBanner = false;
    this.stopBannerDismissTimer();
    if (this.eventId) {
      sessionStorage.setItem(`${LiveChat.BANNER_STORAGE_PREFIX}${this.eventId}`, "true");
    }
  }

  private isBannerDismissedForEvent(): boolean {
    if (!this.eventId) return false;
    return sessionStorage.getItem(`${LiveChat.BANNER_STORAGE_PREFIX}${this.eventId}`) === "true";
  }

  private renderInput(): TemplateResult {
    const isSending = this.chatState?.isSending.get() ?? false;
    const connectionStatus = this.chatState?.connectionStatus.get() ?? "idle";
    const isDisabled = connectionStatus !== "connected";

    return html`
      <div class="input-container" @focusin=${this.handleInputFocusIn} @focusout=${this.handleInputFocusOut}>
        ${this.renderEmojiPicker()}
        <chat-message-input
          placeholder="Send a message..."
          ?disabled=${isDisabled}
          ?sending=${isSending}
          ?show-notify-toggle=${this.isEventOwner}
          ?allow-attachments=${this.isEventOwner}
          notify-label=${getValue(getLang(), "liveChat.notifyToggle") || "Notify your fans of this message via email."}
          @send-message=${this.handleSendMessage}
          @emoji-toggle=${this.handleEmojiToggle}
        ></chat-message-input>
      </div>
    `;
  }

  private handleCloseChat(): void {
    this.dispatchEvent(new CustomEvent("close-chat", { bubbles: true, composed: true }));
  }

  private renderHeaderTop() {
    if (this.hideHeaderTop === true) {
      return nothing;
    }

    const iconStyle = this.primaryColor ? `color: ${this.primaryColor}` : "";
    const count = this.displayViewerCount;
    return html`
      <div class="chat-header-top">
        <span class="chat-header-title">Livechat</span>
        <div class="chat-header-actions">
          ${!this.hideViewerCount && (count > 0 || this.eventStatus === "live" || this.isLobbyOpen)
            ? html`
                <event-badge hide-dot label=${count.toString()}>
                  <ph-users slot="icon" size="16" weight="regular" style=${iconStyle}></ph-users>
                </event-badge>
              `
            : nothing}
          ${this.showCloseButton
            ? html`
                <button class="chat-close-button" @click=${this.handleCloseChat} aria-label="Close chat">
                  <ph-x size="18"></ph-x>
                </button>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  private renderHeader(): TemplateResult {
    return html`
      <div class="chat-header">
        ${this.renderHeaderTop()}
        ${this.isAuthenticated && this.showGuidelinesBanner ? this.renderGuidelinesBanner() : nothing}
      </div>
    `;
  }

  override render(): TemplateResult {
    // For scheduled events before lobby opens, show waiting message instead of chat
    if (this.eventStatus === "scheduled" && !this.isLobbyOpen) {
      return html` <div class="chat-container">${this.renderHeader()} ${this.renderWaitingForEvent()}</div> `;
    }

    const status = this.chatState?.connectionStatus.get() ?? "idle";
    const showInputArea = status !== "loading" && status !== "error" && !this.hideInput;

    return html`
      <div class="chat-container">
        ${this.renderHeader()}
        ${status === "loading" ? this.renderLoading() : status === "error" ? this.renderError() : this.renderMessages()}
        ${showInputArea ? (this.isAuthenticated ? this.renderInput() : this.renderLoginPrompt()) : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-chat": LiveChat;
  }
}
